import React, { useEffect, useState } from 'react';
import {
  FaUserTie, FaUsers, FaCar, FaBook, FaFileAlt,
  FaCalendarAlt, FaMoneyBill, FaStar, FaChartLine
} from 'react-icons/fa';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  getStudents, getBookings, getInstructors, getPayments, getCars, getCourses, getSchoolOwners
} from '../services/api';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const [stats, setStats] = useState({
    students: 0, bookings: 0, instructors: 0, liveTrainers: 0,
    payments: 0, cars: 0, courses: 0, schools: 0, revenue: 0,
  });
  const [recentBookings, setRecentBookings] = useState([]);
  const [weeklyBookingData, setWeeklyBookingData] = useState([]);
  const [monthlyRevenueData, setMonthlyRevenueData] = useState([]);
  const [pendingInstructors, setPendingInstructors] = useState([]);
  const [pendingCars, setPendingCars] = useState([]);
  const [pendingSchools, setPendingSchools] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [licenseRatio, setLicenseRatio] = useState([]);
  const [todayBookings, setTodayBookings] = useState([]);
  const [flaggedInstructors, setFlaggedInstructors] = useState([]);
  const [flaggedSchools, setFlaggedSchools] = useState([]);
  const [userActivityStats, setUserActivityStats] = useState({
    active:2,
    inactive: 526,
  });

  const Tile = ({ title, subtitle, right, children, onClick, className = '' }) => (
    <div
      onClick={onClick}
      className={`rounded-2xl p-4 bg-white/80 dark:bg-neutral-900/70 border border-white/20
                 shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)]
                 transition-shadow cursor-pointer ${className}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">{title}</div>
        {right}
      </div>
      {subtitle && <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">{subtitle}</div>}
      {children}
    </div>
  );


  const formatCurrency = (n) =>
    `₹${(Number(n || 0)).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

  // Counts
  const pendingCounts = {
    instructors: pendingInstructors.length,
    schools: pendingSchools.length,
    cars: pendingCars.length,
  };
  const totalPending = pendingCounts.instructors + pendingCounts.schools + pendingCounts.cars;

  // Limit to 3 per card for display
  const topInstructors = pendingInstructors.slice(0, 3);
  const topSchools = pendingSchools.slice(0, 3);
  const topCars = pendingCars.slice(0, 3);
  const formatPendingSince = (timestamp) => {
    if (!timestamp?.seconds) return 'N/A';
    const date = new Date(timestamp.seconds * 1000);
    const daysDiff = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
    return `${date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} (${daysDiff}d)`;
  };

  // put near top (helpers/styles)
  const PENDING_CARD_HEIGHT = "h-[400px]"; // tweak as you like
  const cardBase = "bg-white dark:bg-neutral-800 border border-blue-100 dark:border-neutral-700 rounded-2xl shadow";
  const cardSoft = "bg-white/80 dark:bg-neutral-800/80 border border-blue-100 dark:border-neutral-700 rounded-xl shadow-md";
  const listItemClass =
    "flex items-center justify-between bg-gray-50 dark:bg-neutral-700 rounded-lg px-3 py-2 border border-gray-100 dark:border-neutral-600";
  const badge = (n) => (
    <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800">{n}</span>
  );
  const navigate = useNavigate();
  const go = (path, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    navigate(qs ? `${path}?${qs}` : path);
  };

  const onLicenseSliceClick = (data) => {
    const license = data?.name === 'With License' ? 'Required' : 'Not Required';
    go('/bookings', { license });
  };

  useEffect(() => {
    const fetchData = async () => {
      const [students, bookings, instructors, payments, cars, courses, schools] = await Promise.all([
        getStudents(), getBookings(), getInstructors(), getPayments(), getCars(), getCourses(), getSchoolOwners()
      ]);

      const revenue = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
      const recent = [...bookings].sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)).slice(0, 5);
      const live = instructors.filter(i => i.isTrainerLive).length;

      const weeklyData = Array(7).fill(0);
      const monthlyMap = {};
      const today = new Date();
      const todayStart = new Date(today.setHours(0, 0, 0, 0)).getTime();
      const todayEnd = new Date(today.setHours(23, 59, 59, 999)).getTime();

      const todays = bookings.filter(b => {
        const start = b.startDate?.seconds ? new Date(b.startDate.seconds * 1000).getTime() : null;
        return start && start >= todayStart && start <= todayEnd;
      });

      // Optional: sort by timeSlot alphabetically or based on custom order
      todays.sort((a, b) => (a.timeSlot || '').localeCompare(b.timeSlot || ''));

      bookings.forEach(booking => {
        const created = new Date((booking.createdAt?.seconds || 0) * 1000);
        const weekDiff = Math.floor((today - created) / (1000 * 60 * 60 * 24));
        if (weekDiff >= 0 && weekDiff < 7) weeklyData[6 - weekDiff]++;
        const monthKey = `${created.getFullYear()}-${created.getMonth() + 1}`;
        monthlyMap[monthKey] = (monthlyMap[monthKey] || 0) + (parseFloat(booking.amount) || 0);
      });

      const monthlyChart = Object.entries(monthlyMap).map(([month, amount]) => ({
        month, revenue: amount
      })).sort((a, b) => new Date(a.month) - new Date(b.month));

      const upcomingMap = Array(7).fill(0);
      bookings.forEach(booking => {
        if (!booking.startDate?.seconds) return;
        const start = new Date(booking.startDate.seconds * 1000);
        const diff = Math.floor((start - today) / (1000 * 60 * 60 * 24));
        if (diff >= 0 && diff < 7) upcomingMap[diff]++;
      });
      const upcomingData = upcomingMap.map((count, i) => ({
        day: ['Today', 'Tomorrow', 'Day 3', 'Day 4', 'Day 5', 'Day 6', 'Day 7'][i],
        bookings: count
      }));

      const withLicense = bookings.filter(b => b.license === 'Required').length;
      const withoutLicense = bookings.length - withLicense;

      const activeStudentIds = new Set(bookings.map(b => b.studentId));

      const activeCount = students.filter(s => activeStudentIds.has(s.studentId)).length;
      const inactiveCount = students.length - activeCount;

      setUserActivityStats({
        active: activeCount,
        inactive: inactiveCount,
      });

      // Instructors without documents
      const instructorsNoDocs = instructors.filter(i => !i.documents || Object.keys(i.documents).length === 0);

      // Schools with 0 documents
      const schoolsNoDocs = schools.filter(s => !s.documents || Object.keys(s.documents).length === 0);

      setFlaggedInstructors(instructorsNoDocs);
      setFlaggedSchools(schoolsNoDocs);

      setStats({
        students: students.length,
        bookings: bookings.length,
        instructors: instructors.length,
        liveTrainers: live,
        payments: payments.length,
        cars: cars.length,
        courses: courses.length,
        schools: schools.length,
        revenue
      });

      setTodayBookings(todays);
      setRecentBookings(recent);
      setWeeklyBookingData(weeklyData.map((count, i) => ({ day: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][(today.getDay() + i + 1) % 7], bookings: count })));
      setMonthlyRevenueData(monthlyChart);
      setUpcomingBookings(upcomingData);
      setLicenseRatio([
        { name: 'With License', value: withLicense },
        { name: 'Without License', value: withoutLicense }
      ]);
      setPendingInstructors(instructors.filter(i => i.approvalStatus === 'PENDING'));
      setPendingCars(cars.filter(c => c.approvalStatus === 'PENDING'));
      setPendingSchools(schools.filter(s => s.approvalStatus === 'PENDING'));
    };

    fetchData();
  }, []);

  const statCards = [
    { icon: <FaUsers />, label: 'Students', value: stats.students },
    { icon: <FaFileAlt />, label: 'Schools', value: stats.schools },
    { icon: <FaUserTie />, label: 'Instructors', value: stats.instructors },
    { icon: <FaCar />, label: 'Vehicles', value: stats.cars },
    { icon: <FaCalendarAlt />, label: 'Bookings', value: stats.bookings },
    { icon: <FaStar />, label: 'Revenue', value: `₹${stats.revenue.toLocaleString()}` },
    { icon: <FaUserTie />, label: 'Live Trainers', value: stats.liveTrainers },
    { icon: <FaMoneyBill />, label: 'Payments', value: stats.payments },
    { icon: <FaBook />, label: 'Courses', value: stats.courses },
  ];

  return (
    <div className="flex flex-col lg:flex-row gap-4 p-6 font-poppins  dark:text-gray-100">
      {/* Sticky Stats Sidebar */}
      <div className="w-full lg:w-[160px] flex-shrink-0 space-y-4 sticky top-6 h-fit" >
        {statCards.map((stat, i) => (
          <div key={i} className="bg-white dark:bg-neutral-800 border-l-4 border-driveeasy-blue shadow rounded p-4 w-[150px]" >
            <div className="flex items-center gap-3">
              <div className="text-xl text-driveeasy-blue">{stat.icon}</div>
              <div>
                <div className="text-gray-600 dark:text-gray-300  text-sm">{stat.label}</div>
                <div className="text-lg font-bold text-black dark:text-gray-100">{stat.value}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Area */}
      <div className="w-full flex-1 space-y-6">
        {/* Charts Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
          <div className="md:col-span-2 ${cardSoft} backdrop-blur-lg border dark:border-neutral-700 rounded-2xl p-5 h-[250px] cursor-pointer" onClick={() => go('/bookings')}>
              <h3 className="text-base font-semibold text-driveeasy-blue mb-2">📊 Weekly Bookings</h3>
              <ResponsiveContainer width="100%" height={190}>
                <LineChart data={weeklyBookingData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="bookings" stroke="#3B82F6" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>

          <div className="md:col-span-1 grid grid-cols-3 gap-3 h-[250px]">
          {/* 2a) Active / Inactive counts (fills half height) */}
          <div className="col-span-1 grid grid-rows-2 gap-3">
            <div className="bg-green-100 text-green-800 rounded-xl shadow flex flex-col items-center justify-center cursor-pointer" onClick={() => go('/users', { status: 'active' })}>
              <div className="text-xl font-bold leading-tight">{userActivityStats.active}</div>
              <div className="text-xs">Active</div>
            </div>
            <div className="bg-red-100 text-red-800 rounded-xl shadow flex flex-col items-center justify-center cursor-pointer" onClick={() => go('/users', { status: 'inactive' })}>
              <div className="text-xl font-bold leading-tight">{userActivityStats.inactive}</div>
              <div className="text-xs">Inactive</div>
            </div>
          </div>


            <div className="col-span-2 bg-white dark:bg-neutral-800 border dark:border-neutral-700 rounded-xl shadow p-4 cursor-pointer" onClick={() => go('/users')}>
              <h3 className="text-sm font-semibold text-driveeasy-blue mb-2">📊 Active vs Inactive</h3>
              {userActivityStats.active + userActivityStats.inactive === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-xs">No student data available.</p>
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Active', value: userActivityStats.active },
                        { name: 'Inactive', value: userActivityStats.inactive },
                      ]}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={38}
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                    >
                      <Cell fill="#10B981" />
                      <Cell fill="#F87171" />
                    </Pie>
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        {/* Row: Payments (30%) | Monthly Revenue (50%) | License (20%) */}
        <div className="grid grid-cols-1 md:grid-cols-10 gap-4 items-stretch">
          {/* Payments – 30% */}
          <div className="col-span-1 md:col-span-3">
            <Tile
              title="Revenue"
              subtitle="Total Collected"
              right={<FaMoneyBill className="text-driveeasy-blue text-xl" />}
              className="h-[250px] flex flex-col cursor-pointer"
              onClick={() => go('/payments')}
            >
              <div className="flex-1 flex flex-col justify-between">
                {/* Main Amount */}
                <div>
                  <div className="text-5xl font-bold text-driveeasy-blue">
                    {formatCurrency(stats.revenue)}
                  </div>
                  {stats.lastPaymentDate && (
                    <div className="text-xs text-neutral-500 mt-1">
                      Last Payment: {new Date(stats.lastPaymentDate).toLocaleDateString()}
                    </div>
                  )}
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-3 gap-3 text-xs text-neutral-600 mt-3">
                  <div className="bg-blue-50 p-2 rounded-lg text-center">
                    <div className="font-semibold text-neutral-800">{stats.payments}</div>
                    <div>Payments</div>
                  </div>
                  <div className="bg-green-50 p-2 rounded-lg text-center">
                    <div className="font-semibold text-neutral-800">
                      {formatCurrency(stats.revenue / (stats.payments || 1))}
                    </div>
                    <div>Avg. Payment</div>
                  </div>
                  <div className="bg-yellow-50 p-2 rounded-lg text-center">
                    <div className="font-semibold text-neutral-800">
                      {formatCurrency(Math.max(...(stats.paymentsList || [0])))}
                    </div>
                    <div>Max Payment</div>
                  </div>
                </div>
              </div>
            </Tile>
          </div>

          {/* Monthly Revenue – 50% */}
          <div className="col-span-1 md:col-span-4 ${cardBase} border dark:border-neutral-700 rounded-2xl p-4 shadow h-[250px] cursor-pointer" onClick={() => go('/payments')}>
              <h3 className="font-semibold text-driveeasy-blue mb-2">💰 Monthly Revenue</h3>
              <ResponsiveContainer width="100%" height={190}>
                <BarChart data={monthlyRevenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="revenue" fill="#F59E0B" />
                </BarChart>
              </ResponsiveContainer>
            </div>

          {/* License Preference – 20% */}
          <div className="col-span-1 md:col-span-3">
            <div className="${cardBase} border dark:border-neutral-700 rounded-2xl p-4 shadow h-[250px]">
              <h3 className="font-semibold text-driveeasy-blue mb-2">🎓 License Preference</h3>
              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie
                    data={licenseRatio}
                    dataKey="value"
                    nameKey="name"
                    cx="50%" cy="50%"
                    outerRadius={60}
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                    onClick={(_, idx, e) => onLicenseSliceClick(_)} // per-slice navigation
                  >
                    <Cell fill="#3B82F6" />
                    <Cell fill="#FBBF24" />
                  </Pie>
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Row: Pending – Instructors (30%) | Schools (30%) | Cars (30%) | Total (10%) */}
        <div className="grid grid-cols-1 md:grid-cols-10 gap-4 items-stretch">
          {/* Instructors – 30% */}
          <div className="col-span-1 md:col-span-3">
            <div className="relative bg-white dark:bg-neutral-800 border dark:border-neutral-700 rounded-2xl shadow p-4 h-[260px]">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-driveeasy-blue font-semibold">⏳ Instructors Pending</h3>
                <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800">
                  {pendingCounts.instructors}
                </span>
              </div>
              <ul className="space-y-2 text-sm mt-3  ">
                {topInstructors.length === 0 ? (
                  <li className="text-green-600 font-medium">🎉 All instructors are verified and active!</li>
                ) : (
                  topInstructors.map((i) => (
                    <li
                      key={i.instructorId}
                      className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
                    >
                      <button
                          className="font-medium text-driveeasy-blue hover:underline"
                          onClick={() => go('/instructors', { instructorId: i.instructorId })}
                        >
                          {i.instructorId || 'N/A'}
                        </button>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                            {formatPendingSince(i.createdAt)}
                          </span>
                    </li>
                  ))
                )}
              </ul>
              <div className="absolute bottom-4 left-4 text-[11px] text-gray-500 dark:text-gray-400">
                Showing {topInstructors.length}/{pendingCounts.instructors}
              </div>
            </div>
          </div>

          {/* Schools – 30% */}
          <div className="col-span-1 md:col-span-3">
            <div className="relative bg-white dark:bg-neutral-800 border dark:border-neutral-700 rounded-2xl shadow p-4 h-[260px]">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-driveeasy-blue font-semibold">🏫 Schools Pending</h3>
                <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800">
                  {pendingCounts.schools}
                </span>
              </div>
              <ul className="space-y-2 text-sm mt-3">
                {topSchools.length === 0 ? (
                  <li className="text-green-600 font-medium">🎉 All schools are verified and onboarded!</li>
                ) : (
                  topSchools.map((s) => (
                    <li
                      key={s.uid}
                      className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
                    >
                      <button
                          className="font-medium text-driveeasy-blue hover:underline"
                          onClick={() => go('/school-owners', { schoolOwnerId: s.uid })}
                        >
                          {s.uid || 'N/A'}
                        </button>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                            {formatPendingSince(s.createdAt)}
                          </span>
                    </li>
                  ))
                )}
              </ul>
              <div className="absolute bottom-4 left-4 text-[11px] text-gray-500 dark:text-gray-400">
                Showing {topSchools.length}/{pendingCounts.schools}
              </div>
            </div>
          </div>

          {/* Cars – 30% */}
          <div className="col-span-1 md:col-span-3">
            <div className="relative bg-white dark:bg-neutral-800 border dark:border-neutral-700 rounded-2xl shadow p-4 h-[260px]">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-driveeasy-blue font-semibold">🚗 Vehicles Pending</h3>
                <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800">
                  {pendingCounts.cars}
                </span>
              </div>
              <ul className="space-y-2 text-sm mt-3">
                {topCars.length === 0 ? (
                  <li className="text-green-600 font-medium">🎉 All vehicles are approved and ready!</li>
                ) : (
                  topCars.map((c) => (
                    <li
                      key={c.carId}
                      className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
                    >
                      <button
                          className="font-medium text-driveeasy-blue hover:underline"
                          onClick={() => go('/vehicles', { carId: c.carId })}
                        >
                          {c.carId || 'N/A'}
                        </button>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                            {formatPendingSince(c.createdAt)}
                          </span>
                    </li>
                  ))
                )}
              </ul>
              <div className="absolute bottom-4 left-4 text-[11px] text-gray-500 dark:text-gray-400">
                Showing {topCars.length}/{pendingCounts.cars}
              </div>
            </div>
          </div>

          {/* Total – 10% */}
          <div className="col-span-1 md:col-span-1">
            <div className="bg-white dark:bg-neutral-800 border dark:border-neutral-700 rounded-2xl shadow p-4 h-[260px] flex flex-col items-center justify-center">
              <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Total Pending</div>
              <div className="text-4xl font-bold text-driveeasy-blue mt-1">{totalPending}</div>
              <div className="mt-3 text-xs text-gray-600 dark:text-gray-300 text-center leading-5">
                <div><span className="font-semibold text-yellow-700">{pendingCounts.instructors}</span> Instructors</div>
                <div><span className="font-semibold text-yellow-700">{pendingCounts.schools}</span> Schools</div>
                <div><span className="font-semibold text-yellow-700">{pendingCounts.cars}</span> Cars</div>
              </div>
            </div>
          </div>
        </div>


        {/* Row 4: Recent Bookings (40%) | Today's Timeline (60%) */}
        <div className="grid grid-cols-1 md:grid-cols-10 gap-4 items-stretch">
          {/* Recent Bookings – 40% */}
          <div className="col-span-1 md:col-span-4">
            <div className="bg-white/90 dark:bg-neutral-800/80 border dark:border-neutral-700 rounded-2xl shadow p-4 h-[280px] flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-driveeasy-blue">🧾 Recent Bookings</h3>
                <span className="text-xs text-gray-500">
                  Showing {Math.min(5, recentBookings.length)}/{recentBookings.length}
                </span>
              </div>

              <div className="rounded-lg border border-gray-100 dark:border-neutral-700 overflow-hidden">
                <table className="w-full text-xs md:text-sm">
                  <thead className="bg-driveeasy-blue text-white dark:bg-driveeasy-blue">
                    <tr>
                      <th className="px-3 py-2 text-left">Booking ID</th>
                      <th className="px-3 py-2 text-left">Student</th>
                      <th className="px-3 py-2 text-left">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {recentBookings.slice(0, 5).map((b) => (
                      <tr key={b.bookingId} className="hover:bg-gray-50 dark:hover:bg-neutral-700">
                        <td className="px-3 py-2">
                          <button
                            className="text-blue-600 font-medium hover:underline"
                            onClick={() => go('/bookings', { bookingId: b.bookingId })}
                          >
                            {b.bookingId}
                          </button>
                        </td>
                        <td className="px-3 py-2 truncate">{b.studentName || 'N/A'}</td>
                        <td className="px-3 py-2 font-semibold">₹{b.amount ?? '—'}</td>
                      </tr>
                    ))}
                    {recentBookings.length === 0 && (
                      <tr>
                        <td colSpan={3} className="px-3 py-6 text-center text-gray-500 dark:text-gray-400">No recent bookings</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Footer action / hint */}
              <div className="mt-auto pt-3 text-right">
                <button className="text-xs text-driveeasy-blue hover:underline">
                  View all bookings →
                </button>
              </div>
            </div>
          </div>

          {/* Today's Timeline – 60% */}
          <div className="col-span-1 md:col-span-6">
            <div className="bg-white/90 dark:bg-neutral-800/80 border dark:border-neutral-700 rounded-2xl shadow p-4 h-[280px] flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-driveeasy-blue">🕣 Today’s Bookings Timeline</h3>
                <span className="text-xs text-gray-500">{todayBookings.length} items</span>
              </div>

              {todayBookings.length === 0 ? (
                <div className="flex-1 grid place-items-center text-gray-500 dark:text-gray-400">No bookings scheduled for today.</div>
              ) : (
                <div className="flex-1 overflow-x-auto">
                  <div className="flex space-x-4 min-w-max pr-2">
                    {todayBookings.map((b, idx) => (
                      <div
                        key={b.bookingId}
                        className="relative w-56 flex-shrink-0 bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-xl p-3 shadow-sm cursor-pointer" onClick={() => go('/bookings', { bookingId: b.bookingId })}
                      >
                        {/* dot + connector */}
                        <div className="absolute -top-2 left-3 w-3 h-3 bg-driveeasy-yellow rounded-full ring-2 ring-white shadow" />
                        {idx < todayBookings.length - 1 && (
                          <div className="absolute top-[-0.25rem] left-6 right-[-1rem] h-[2px] bg-blue-200" />
                        )}

                        <div className="text-sm font-semibold text-driveeasy-blue truncate">
                          {b.studentName || 'N/A'}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">{b.timeSlot || 'No Time'}</div>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-gray-500">
                          <div className="truncate">Stu: <span className="font-medium text-gray-700">{b.studentId}</span></div>
                          <div className="truncate">ID: <span className="font-medium text-gray-700">{b.bookingId}</span></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Footer legend */}
              <div className="pt-3 text-[11px] text-gray-500 flex items-center gap-3">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-driveeasy-yellow" /> upcoming slot
                <span className="ml-auto">Scroll →</span>
              </div>
            </div>
          </div>
        </div>

          {/* Row 5: Warnings – split into two uniform cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Instructors Warnings */}
            <div className={'bg-white dark:bg-neutral-800 border-l-4 border-yellow-500 rounded-2xl p-4 shadow ${PENDING_CARD_HEIGHT} flex flex-col'}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-yellow-600">⚠️ Instructors Missing Docs</h3>
                {badge(flaggedInstructors.length)}
              </div>

              {flaggedInstructors.length === 0 ? (
                <p className="text-gray-600 dark:text-gray-300 mt-2">✅ No issues found. Everything looks good!</p>
              ) : (
                <>
                  <ul className="mt-3 space-y-2 text-sm overflow-y-auto">
                    {flaggedInstructors.slice(0, 8).map((i, idx) => (
                      <li key={idx} className={listItemClass}>
                        <button
                            className="font-medium text-driveeasy-blue hover:underline"
                            onClick={() => go('/instructors', { instructorName: i.instructorName })}
                          >
                            {i.instructorName || 'N/A'}
                          </button>
                        <span className="text-xs text-gray-500">{i.phoneNumber || ''}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 text-[11px] text-gray-500">
                    Showing {Math.min(8, flaggedInstructors.length)}/{flaggedInstructors.length}
                  </div>
                </>
              )}
            </div>

            {/* Schools Warnings */}
            <div className={'bg-white dark:bg-neutral-800 border-l-4 border-red-500 rounded-2xl p-4 shadow ${PENDING_CARD_HEIGHT} flex flex-col'}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-red-600">🚩 Schools w/o Documents</h3>
                {badge(flaggedSchools.length)}
              </div>

              {flaggedSchools.length === 0 ? (
                <p className="text-gray-600 dark:text-gray-300 mt-2">✅ No issues found. Everything looks good!</p>
              ) : (
                <>
                  <ul className="mt-3 space-y-2 text-sm overflow-y-auto">
                    {flaggedSchools.slice(0, 8).map((s, idx) => (
                      <li key={idx} className={listItemClass}>
                        <span className="font-medium">{s.schoolName || 'N/A'}</span>
                        <button
                            className="text-xs text-driveeasy-blue hover:underline"
                            onClick={() => go('/school-owners', { phoneNumber: s.phoneNumber })}
                          >
                            {s.phoneNumber || ''}
                          </button>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 text-[11px] text-gray-500">
                    Showing {Math.min(8, flaggedSchools.length)}/{flaggedSchools.length}
                  </div>
                </>
              )}
            </div>
          </div>
      </div>
    </div>
  );
};

export default Dashboard;
